import { useEffect, useState } from 'react';
import { Button, Card, Stack, Cluster, Text, Badge, Modal } from './ui';
import { AddressForm } from './AddressForm';
import { useToast } from './Toast';
import {
  fetchMyAddresses, saveAddress, deleteAddress, setDefaultAddress,
  type ShippingAddress,
} from '../lib/queries';
import { COUNTRIES } from '../lib/countries';

/**
 * The member's delivery address book (profile > Addresses). Add, edit, remove,
 * and choose a default. The default is what a lot ships to unless the member
 * picks another address on the lot itself.
 */

function countryName(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

export function AddressBook() {
  const toast = useToast();
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [loaded, setLoaded] = useState(false);
  // null = closed; 'new' = add; an address = edit.
  const [editing, setEditing] = useState<'new' | ShippingAddress | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function load() {
    return fetchMyAddresses()
      .then(setAddresses)
      .catch(() => { /* leave as-is */ })
      .finally(() => setLoaded(true));
  }
  useEffect(() => { void load(); }, []);

  async function handleSave(addr: ShippingAddress, makeDefault: boolean) {
    await saveAddress(addr, makeDefault);
    setEditing(null);
    toast.success(addr.id ? 'Address updated.' : 'Address added.');
    await load();
  }

  async function makeDefault(id: string) {
    setBusyId(id);
    try {
      await setDefaultAddress(id);
      await load();
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not set the default.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(addr: ShippingAddress) {
    if (!addr.id) return;
    if (!window.confirm('Remove this address?')) return;
    setBusyId(addr.id);
    try {
      await deleteAddress(addr.id);
      await load();
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not remove the address.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="profile__section">
      <Cluster justify="between" align="center" gap="group">
        <p className="kicker">Delivery addresses</p>
        <Button type="button" variant="soft" size="sm" onClick={() => setEditing('new')}>
          Add address
        </Button>
      </Cluster>

      <Text role="small" muted>
        Where your wins are posted. Your default is used unless you pick another
        address on a lot.
      </Text>

      {!loaded ? (
        <Text role="small" muted className="mt-3">Loading…</Text>
      ) : addresses.length === 0 ? (
        <Card surface="bone" pad="md" className="mt-3">
          <Text role="small" muted>
            No saved addresses yet. Add one so we can post your wins and show
            accurate shipping.
          </Text>
        </Card>
      ) : (
        <Stack gap="group" className="mt-3">
          {addresses.map((a) => (
            <Card key={a.id} bordered pad="md">
              <Cluster justify="between" align="start" gap="group" wrap>
                <Stack gap="tight">
                  <Cluster gap="tight" align="center">
                    <Text role="label" as="span">{a.label || a.full_name}</Text>
                    {a.is_default && <Badge tone="accent">Default</Badge>}
                  </Cluster>
                  <Text role="small" muted>
                    {a.label ? <>{a.full_name}<br /></> : null}
                    {a.street1}{a.street2 ? `, ${a.street2}` : ''}<br />
                    {a.city}{a.state ? `, ${a.state}` : ''} {a.postal_code}<br />
                    {countryName(a.country)}
                    {a.phone ? <><br />{a.phone}</> : null}
                  </Text>
                </Stack>
                <Cluster gap="tight" wrap>
                  {!a.is_default && (
                    <Button type="button" variant="ghost" size="sm"
                            onClick={() => a.id && makeDefault(a.id)} disabled={busyId === a.id}>
                      Make default
                    </Button>
                  )}
                  <Button type="button" variant="ghost" size="sm"
                          onClick={() => setEditing(a)} disabled={busyId === a.id}>
                    Edit
                  </Button>
                  <Button type="button" variant="ghost" size="sm" tone="danger"
                          onClick={() => remove(a)} disabled={busyId === a.id}>
                    Remove
                  </Button>
                </Cluster>
              </Cluster>
            </Card>
          ))}
        </Stack>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add address' : 'Edit address'}
        size="md"
      >
        {editing !== null && (
          <AddressForm
            initial={editing === 'new' ? null : editing}
            submitLabel={editing === 'new' ? 'Add address' : 'Save changes'}
            showDefaultToggle={addresses.length > 0 && (editing === 'new' || !editing.is_default)}
            onSubmit={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </section>
  );
}
